import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import {
    message,
    Upload,
    Input,
    Button,
    Image,
    Select,
    ColorPicker,
    Row,
    Col,
    Divider,
} from "antd";
import { Helmet } from "react-helmet";
import { Layout } from "../pages/app/Layout";
import { Loader } from "../components/Loader";
import { PBox, PH4, PImg, PText, PH2, PH1 } from "../theme/BaseTheme";
import { refetchUser } from "../../actions/auth";
import ChangePlanModal from "../components/app/ChangePlanModal";
import PaymentConfirmModal from "../components/app/PaymentConfirmModal";
import { SellFaq } from "../sections/SellFaq";
import { outline_maker_faq_data } from "./OutlineMakerLanding";
import { generateOutline } from "../../actions/outline";
import OutlineMakerDownloadModal from "./OutlineMakerDownloadModal";
import { UploadErrorMessages } from "../../data/messages";
import {
    red,
    volcano,
    orange,
    lime,
    gold,
    yellow,
    green,
    cyan,
    blue,
    purple,
    magenta,
} from "@ant-design/colors";

const OutlineMakerCreditCost = 2;

const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

const styleOptions = [
    {
        value: "single",
        label: "Single",
        image: "/images/outline/style-1.webp",
    },
    {
        value: "double",
        label: "Double",
        image: "/images/outline/style-2.webp",
    },
    {
        value: "dual",
        label: "Dual",
        image: "/images/outline/style-3.webp",
    },
];

function genPresets(presets = customPresets) {
    console.log(presets);
    return Object.entries(presets).map(([label, colors]) => ({
        label,
        colors,
        key: label,
    }));
}

const customPresets = {
    Vibrant: [
        "#FF2400",
        "#4169E1",
        "#FF7F00",
        "#50C878",
        "#FF69B4",
        "#FFD300",
        "#40E0D0",
        "#5D3FD3",
        "#DC143C",
        "#008080",
    ],
    Pastel: [
        "#FFC1CC",
        "#A2CFFE",
        "#B6FCD5",
        "#E6E6FA",
        "#FFDAB9",
        "#FFF5B7",
        "#C8A2C8",
        "#FFD1A9",
        "#A7E8BD",
        "#B0E0E6",
    ],
    Neon: [
        "#FF10F0",
        "#39FF14",
        "#1F51FF",
        "#FFFF33",
        "#FF5F1F",
        "#B026FF",
        "#00FFFF",
        "#FF073A",
        "#CCFF00",
        "#FF00FF",
    ],
    red,
    volcano,
    orange,
    lime,
    gold,
    yellow,
    green,
    cyan,
    blue,
    purple,
    magenta,
};

const customPanelRender = (_, { components: { Picker, Presets } }) => (
    <Row justify="space-between" wrap={false}>
        <Col span={12} style={{ maxHeight: "240px", overflowY: "auto" }}>
            <Presets />
        </Col>
        <Divider type="vertical" style={{ height: "auto" }} />
        <Col flex="auto">
            <Picker />
        </Col>
    </Row>
);

const OutlineMakerApp = (props) => {
    const highlightRef = useRef();
    const [isLoading, setIsLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [uploadedImage, setUploadedImage] = useState("");
    const [outline, setOutline] = useState("");
    const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);
    const [isPaymentConfirmModalOpen, setIsPaymentConfirmModalOpen] =
        useState(false);
    const [newPlan, setNewPlan] = useState("");
    const [shouldScrollToResult, setShouldScrollToResult] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const [style, setStyle] = useState("single");
    const [colorOne, setColorOne] = useState("#000000");
    const [colorTwo, setColorTwo] = useState("#000000");
    const [backgroundColor, setBackgroundColor] = useState({
        color: "#000000",
        percent: 0,
    });
    const presets = genPresets(customPresets);

    const handleUpload = async ({ file }) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setUploadedImage(file.url || file.preview);
    };

    useEffect(() => {
        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (shouldScrollToResult && highlightRef.current) {
            highlightRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            setShouldScrollToResult(false);
        }
    }, [shouldScrollToResult]);

    const handleDownloadModalOk = () => {
        setIsDownloadModalOpen(false);
    };

    const handleDownloadModalCancel = () => {
        setIsDownloadModalOpen(false);
    };

    const renderContent = () => {
        if (isLoading) {
            return <Loader />;
        }
        return (
            <Layout>
                <Helmet>
                    <title>Outline Maker App</title>
                    <meta
                        name="description"
                        content="AI-powered outline maker to make outlines for your image."
                    />
                    <meta
                        name="keywords"
                        content="Outline Maker, AI Outline Maker, Outline Maker APP, Outline Online Maker, Image Processing, AI Image Editing, Patternful, Patternful AI"
                    />
                </Helmet>
                <PBox css={styles}>
                    <PBox className="app-content">
                        <PBox className="app-content-title">
                            <PH1>AI Outline Maker</PH1>
                            <PText>
                                Add professional outlines to product pictures
                                effortlessly. Create stickers and memes
                                instantly.
                            </PText>
                        </PBox>
                        <PBox className="app-content-control">
                            <PBox className="outline-maker-upload-image">
                                <PBox className="upload-section">
                                    <PBox className="outline-maker-upload">
                                        <Upload.Dragger
                                            onChange={async (info) => {
                                                await handleUpload(info);
                                            }}
                                            showUploadList={false}
                                            listType="picture-card"
                                            maxCount={1}
                                            beforeUpload={async (file) => {
                                                const isJpgOrPng =
                                                    file.type ===
                                                        "image/jpeg" ||
                                                    file.type === "image/png";
                                                if (!isJpgOrPng) {
                                                    message.error(
                                                        UploadErrorMessages.JPG_OR_PNG
                                                    );
                                                }
                                                const isLt4M =
                                                    file.size / 1024 / 1024 < 4;
                                                if (!isLt4M) {
                                                    message.error(
                                                        UploadErrorMessages.IMAGE_SIZE_4MB
                                                    );
                                                }
                                                return (
                                                    (isJpgOrPng && isLt4M) ||
                                                    Upload.LIST_IGNORE
                                                );
                                            }}
                                        >
                                            {uploadedImage !== "" && (
                                                <PImg
                                                    className="outline-maker-image"
                                                    src={uploadedImage}
                                                />
                                            )}
                                            {uploadedImage === "" && (
                                                <PText>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="#000000"
                                                        viewBox="0 0 256 256"
                                                    >
                                                        <path d="M224,152v56a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V152a8,8,0,0,1,16,0v56H208V152a8,8,0,0,1,16,0ZM93.66,85.66,120,59.31V152a8,8,0,0,0,16,0V59.31l26.34,26.35a8,8,0,0,0,11.32-11.32l-40-40a8,8,0,0,0-11.32,0l-40,40A8,8,0,0,0,93.66,85.66Z"></path>
                                                    </svg>
                                                    <br />
                                                    Click to upload your image,
                                                    <br />
                                                    or drag and drop your image
                                                    into this area.
                                                </PText>
                                            )}
                                        </Upload.Dragger>
                                    </PBox>
                                </PBox>
                                <PBox className="input-section">
                                    <PText className="control-section-label">
                                        Step 1: Select Style
                                    </PText>
                                    <div className="background-options-grid">
                                        {styleOptions.map((option) => (
                                            <div
                                                key={option.value}
                                                className={`background-option ${
                                                    style === option.value
                                                        ? "selected"
                                                        : ""
                                                }`}
                                                onClick={() => {
                                                    setStyle(option.value);
                                                }}
                                            >
                                                <img
                                                    src={option.image}
                                                    alt={option.label}
                                                />
                                                <span>{option.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <PText className="control-section-label">
                                        Step 2: Select Outline Colors
                                    </PText>
                                    <div className="color-picker-container">
                                        <ColorPicker
                                            showText={(color) => {
                                                return style === "single" ? (
                                                    <span>
                                                        Single Color (
                                                        {color.toHexString()})
                                                    </span>
                                                ) : style === "double" ? (
                                                    <span>
                                                        Inner Color (
                                                        {color.toHexString()})
                                                    </span>
                                                ) : (
                                                    <span>
                                                        Left Color (
                                                        {color.toHexString()})
                                                    </span>
                                                );
                                            }}
                                            onChange={(color) => {
                                                setColorOne(color);
                                            }}
                                            presets={presets}
                                            panelRender={customPanelRender}
                                        />
                                        {style !== "single" && (
                                            <ColorPicker
                                                showText={(color) => {
                                                    return style ===
                                                        "single" ? (
                                                        <span>
                                                            Single Color (
                                                            {color.toHexString()}
                                                            )
                                                        </span>
                                                    ) : style === "double" ? (
                                                        <span>
                                                            Outer Color (
                                                            {color.toHexString()}
                                                            )
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            Right Color (
                                                            {color.toHexString()}
                                                            )
                                                        </span>
                                                    );
                                                }}
                                                onChange={(color) => {
                                                    setColorTwo(color);
                                                }}
                                                presets={presets}
                                                panelRender={customPanelRender}
                                            />
                                        )}
                                    </div>
                                    <PText className="control-section-label">
                                        Step 3: Select Background Color
                                    </PText>
                                    <ColorPicker
                                        allowClear
                                        showText={(color) => {
                                            return (
                                                <span>
                                                    Background Color (
                                                    {color.toHexString()})
                                                </span>
                                            );
                                        }}
                                        onChangeComplete={(color) => {
                                            setBackgroundColor(color);
                                        }}
                                        presets={presets}
                                        panelRender={customPanelRender}
                                    />
                                </PBox>
                            </PBox>
                            <PBox className="generate-button-container">
                                <Button
                                    type="primary"
                                    onClick={async () => {
                                        if (
                                            props.userAccount?.credits +
                                                props.userAccount
                                                    ?.roll_over_credits <
                                            OutlineMakerCreditCost
                                        ) {
                                            message.error(
                                                "Your credit balance is not enough. Please consider upgrading your plan or buying more credits.",
                                                3
                                            );
                                        } else {
                                            if (uploadedImage === "") {
                                                message.error(
                                                    "Please upload an image. ",
                                                    3
                                                );
                                            } else {
                                                setIsGenerating(true);
                                                message.open({
                                                    key: "gen_outline",
                                                    type: "loading",
                                                    content:
                                                        "Start generating outlines. This can take a while.",
                                                    duration: 0,
                                                });
                                                await props.generateOutline(
                                                    uploadedImage,
                                                    style,
                                                    colorOne,
                                                    colorTwo,
                                                    backgroundColor
                                                );
                                                message.destroy("gen_outline");
                                                await props.refetchUser();
                                                setIsGenerating(false);
                                                setShouldScrollToResult(true);
                                            }
                                        }
                                    }}
                                    loading={isGenerating}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    Generate{" "}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="#000000"
                                        viewBox="0 0 256 256"
                                        style={{
                                            fill: "#d48806",
                                            backgroundColor: "#ffe58f",
                                            padding: "0.125rem 0.25rem",
                                            borderRadius: "0.25rem",
                                            width: "1rem",
                                            height: "1rem",
                                            marginLeft: "0.5rem",
                                            marginRight: "0.25rem",
                                        }}
                                    >
                                        <path d="M200,48H136V16a8,8,0,0,0-16,0V48H56A32,32,0,0,0,24,80V192a32,32,0,0,0,32,32H200a32,32,0,0,0,32-32V80A32,32,0,0,0,200,48Zm16,144a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V80A16,16,0,0,1,56,64H200a16,16,0,0,1,16,16Zm-52-56H92a28,28,0,0,0,0,56h72a28,28,0,0,0,0-56Zm-28,16v24H120V152ZM80,164a12,12,0,0,1,12-12h12v24H92A12,12,0,0,1,80,164Zm84,12H152V152h12a12,12,0,0,1,0,24ZM72,108a12,12,0,1,1,12,12A12,12,0,0,1,72,108Zm88,0a12,12,0,1,1,12,12A12,12,0,0,1,160,108Z"></path>
                                    </svg>
                                    x{OutlineMakerCreditCost}
                                </Button>
                                <Button type="ghost" href="/outlines/1">
                                    View My Outlines
                                </Button>
                            </PBox>

                            {props.outline !== undefined && (
                                <>
                                    <PBox
                                        className="generate-outline-content"
                                        ref={highlightRef}
                                    >
                                        <PBox className="binary-mask-sketch-canva">
                                            <Image
                                                src={props.outline}
                                                crossOrigin={
                                                    process.env.NODE_ENV ===
                                                    "development"
                                                        ? "anonymous"
                                                        : ""
                                                }
                                            />
                                        </PBox>
                                    </PBox>{" "}
                                    <PBox className="generate-button-container">
                                        <Button
                                            type="primary"
                                            onClick={async () => {
                                                setIsDownloadModalOpen(true);
                                            }}
                                        >
                                            Download
                                        </Button>
                                    </PBox>
                                </>
                            )}

                            <SellFaq data={outline_maker_faq_data} />
                        </PBox>
                        {isChangePlanModalOpen && (
                            <ChangePlanModal
                                isChangePlanModalOpen={isChangePlanModalOpen}
                                handleChangePlanModalOk={async () => {
                                    setIsPaymentConfirmModalOpen(true);
                                    setIsChangePlanModalOpen(false);
                                }}
                                handleChangePlanModalCancel={() => {
                                    setIsChangePlanModalOpen(false);
                                }}
                                setNewPlan={(value) => {
                                    setNewPlan(value);
                                }}
                            />
                        )}
                        {isDownloadModalOpen && (
                            <OutlineMakerDownloadModal
                                outlineUUID={props.uuid}
                                isDownloadModalOpen={isDownloadModalOpen}
                                handleDownloadModalOk={handleDownloadModalOk}
                                handleDownloadModalCancel={
                                    handleDownloadModalCancel
                                }
                            />
                        )}
                        {isPaymentConfirmModalOpen && (
                            <PaymentConfirmModal
                                newPlan={newPlan}
                                isPaymentConfirmModalOpen={
                                    isPaymentConfirmModalOpen
                                }
                                handlePaymentConfirmModalOk={() =>
                                    setIsPaymentConfirmModalOpen(false)
                                }
                                handlePaymentConfirmModalCancel={() =>
                                    setIsPaymentConfirmModalOpen(false)
                                }
                            />
                        )}
                    </PBox>
                </PBox>
            </Layout>
        );
    };

    return renderContent();
};

const styles = {
    minHeight: "48rem",
    "& .app-content-title": {
        "& h1": {
            textAlign: "center",
        },
        "& p": {
            textAlign: "center",
        },
        margin: "4rem auto",
    },
    "& .app-content-control": {
        margin: "0 auto",
    },
    "& .generate-button-container": {
        display: "flex",
        justifyContent: "center",
        margin: "2rem 0",
        "& button": {
            margin: "0 0.5rem",
        },
    },
    "& .outline-maker-upload-image": {
        margin: "2rem auto 2rem auto",
        maxWidth: "64rem",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "2rem",
        alignItems: "start",
        "@media (max-width: 768px)": {
            gridTemplateColumns: "1fr",
            gap: "1rem",
        },

        "& .upload-section": {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
        },
        "& .input-section": {
            display: "flex",
            flexDirection: "column",
            "& .control-section-label": {
                fontWeight: "600",
                fontSize: "14px",
                color: "$colors$text",
                marginBottom: "1rem",
            },
            "& .background-options-grid": {
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                gap: "1rem",
                height: "130px",
                padding: "0.5rem",
                border: "1px solid $colors$borderColor",
                borderRadius: "8px",
                "&::-webkit-scrollbar": {
                    width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "4px",
                    "&:hover": {
                        background: "#555",
                    },
                },
                "& .background-option": {
                    cursor: "pointer",
                    textAlign: "center",
                    transition: "all 0.2s ease",
                    borderRadius: "8px",
                    padding: "0.5rem",
                    border: "2px solid transparent",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    },
                    "&.selected": {
                        border: "2px solid $colors$primary",
                        backgroundColor: "rgba(24, 144, 255, 0.1)",
                    },
                    "& img": {
                        width: "100%",
                        height: "80px",
                        objectFit: "contain",
                        borderRadius: "4px",
                        marginBottom: "0.5rem",
                    },
                    "& span": {
                        display: "block",
                        fontSize: "12px",
                        color: "$colors$text",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    },
                },
            },
        },
        "& .outline-maker-upload": {
            marginBottom: "1rem",
            width: "100%",
            "& svg": {
                width: "3rem",
                height: "3rem",
                fill: "$colors$primary",
            },
            "& span": {
                padding: "0",
                width: "100%",
                height: "24rem",
                display: "block",
                margin: "0",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            },
        },
        "& .outline-maker-image": {
            maxWidth: "100%",
            maxHeight: "14rem",
            margin: "auto 1rem",
            objectFit: "contain",
        },
        "& h2": {
            marginBottom: "3rem",
        },
    },
    "& .remove-background-download-buttons": {
        margin: "5rem auto",
        display: "flex",
        justifyContent: "center",
        "& button": {
            margin: "0 0.5rem",
        },
    },
    "& .remove-background-content": {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "2rem",
        marginTop: "8rem",
        marginBottom: "4rem",
        "& button": {
            width: "100%",
        },
    },
    "& .binary-mask-sketch-canva": {
        display: "block",
        "& img": {
            display: "block",
            margin: "auto",
            width: `calc(31rem - 2px)`,
        },
    },
    "& .binary-mask-brush-size": {
        textAlign: "left",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "1rem",
        "& h4": { marginLeft: "0.5rem" },
    },
    "& .binary-mask-result-image": {
        margin: "6rem auto 0 auto",
        "& img": {
            height: "40rem",
            margin: "0 auto",
        },
    },
    "& .upscale-option": {
        display: "flex",
        alignItems: "center",
    },
    "& .caption-before": {
        background: "#fff",
        border: "1px solid #c0c0c0",
        borderRadius: "12px",
        color: "#2e3452",
        opacity: "0.8",
        padding: "12px",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        lineHeight: "100%",
        left: "12px",
    },
    "& .caption-after": {
        background: "#fff",
        border: "1px solid #c0c0c0",
        borderRadius: "12px",
        color: "#2e3452",
        opacity: "0.8",
        padding: "12px",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        lineHeight: "100%",
        right: "12px",
    },
    "& .slider-with-animated-handle": {
        position: "relative",
        margin: "auto auto",
        //"& img": { maxHeight: "28rem" },
    },
    "& .remove-background-slider": {
        width: "60rem",
        height: "40rem",
        border: "1px solid $colors$borderColor",
        position: "relative",
        borderRadius: "0.5rem",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "0 auto",
        "& img": {
            //width: "30rem",
            maxHeight: "40rem",
            margin: "0 auto",
        },
    },
    "& .generate-outline-content": {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "8rem auto 2rem auto",
        "& img": {
            maxHeight: "40rem",
        },
    },
};

function mapStateToProps(state) {
    return {
        userAccount: state.authReducer?.userAuth?.userAccount,
        outline: state.outlineReducer?.generateOutline?.generatedImage,
        uuid: state.outlineReducer?.generateOutline?.uuid,
    };
}

export default connect(mapStateToProps, {
    refetchUser,
    generateOutline,
})(withRouter(OutlineMakerApp));
